import { Flex } from '@mantine/core';
import classNames from 'classnames';
import type { TFunction } from 'i18next';
import type { JSX } from 'react';
import React from 'react';
import { Link } from 'react-router-dom';

import type { BaseCampaign } from '@feathr/blackbox';
import { type IColumn, TableColumnHeader, Text, Time } from '@feathr/components';
import CampaignTypeChip from '@feathr/extender/components/CampaignTypeChip';
import { currencyFormatter } from '@feathr/extender/utils';
import { TimeFormat } from '@feathr/hooks';

import * as tableStyles from '@feathr/components/dist/Table/Table.css';
import * as styles from './GoalDetailTopCampaigns.css';

export interface ITopCampaignRow {
  campaign: BaseCampaign;
  firstConversionDate: string;
  id: string;
  lastConversionDate: string;
  totalConversionValue: number;
  totalConversions: number;
}

function getGoalDetailTopCampaignsColumns(
  t: TFunction,
  localUrl: (path: string) => string,
): Array<IColumn<ITopCampaignRow>> {
  return [
    {
      accessor: (row: ITopCampaignRow): string => row.campaign.get('name'),
      id: 'name',
      Header: TableColumnHeader({ sortType: 'alpha', title: t('Campaign') }),
      headerClassName: classNames(tableStyles.cellLeft, tableStyles.sort),
      sortable: true,
      Cell({ original }): JSX.Element {
        const to = localUrl(original.campaign.getItemUrl());
        return <Link to={to}>{original.campaign.get('name')}</Link>;
      },
    },
    {
      accessor: (row: ITopCampaignRow): string => row.campaign.get('_cls'),
      id: '_cls',
      Header: TableColumnHeader({ sortType: 'alpha', title: t('Type') }),
      headerClassName: tableStyles.sort,
      sortable: true,
      Cell({ original }): JSX.Element {
        return <CampaignTypeChip cls={original.campaign.get('_cls')} />;
      },
    },
    {
      id: 'total_conversions',
      Header: TableColumnHeader({ sortType: 'numeric', title: t('Conversions') }),
      headerClassName: tableStyles.sort,
      sortable: true,
      width: 130,
      Cell({ original }): JSX.Element {
        return <Text.DefaultRegular>{original.totalConversions}</Text.DefaultRegular>;
      },
    },
    {
      id: 'total_conversion_value',
      Header: TableColumnHeader({ sortType: 'numeric', title: t('Conversion Value') }),
      headerClassName: tableStyles.sort,
      sortable: true,
      width: 160,
      Cell({ original }): JSX.Element {
        return (
          <Text.DefaultRegular>
            {currencyFormatter.format(original.totalConversionValue)}
          </Text.DefaultRegular>
        );
      },
    },
    {
      accessor: (row: ITopCampaignRow): string => row.firstConversionDate,
      id: 'date_range',
      Header: TableColumnHeader({ sortType: 'numeric', title: t('Date Range') }),
      headerClassName: tableStyles.sort,
      sortable: true,
      width: 220,
      Cell({ original }): JSX.Element {
        return (
          <Flex>
            <Time format={TimeFormat.longDate} timestamp={original.firstConversionDate} />
            <Text.DefaultRegular className={styles.dateSeparator} truncate={false}>
              –
            </Text.DefaultRegular>
            <Time format={TimeFormat.longDate} timestamp={original.lastConversionDate} />
          </Flex>
        );
      },
    },
  ];
}

export default getGoalDetailTopCampaignsColumns;
